import { useUnit } from 'effector-react';
import { Eye, MoreHorizontal, Plus, Power, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $instruments,
  $instrumentsLoaded,
  deleteInstrumentFx,
  instrumentsRefreshed,
  updateInstrumentFx,
} from '../../entities/instrument';
import { $watchlist, marketRefreshed, TrendPill, VolatilityPill } from '../../entities/market';
import { toastShown } from '../../entities/toast';
import { confirmRequested } from '../../features/confirm';
import { addInstrumentOpened } from '../../features/manage-watchlist';
import { openPositionRequested } from '../../features/open-position';
import type { Schemas } from '../../shared/api/client';
import {
  formatMoney,
  formatPercent,
  formatRatio,
  placesOf,
  toNumber,
} from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import {
  Button,
  DataTable,
  EmptyState,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Pill,
  PlValue,
  Spinner,
  type Column,
} from '../../shared/ui';
import { AppShell } from '../../widgets/app-shell';

type Instrument = Schemas['InstrumentOut'];

export function WatchlistPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [instruments, loaded, watchlist, refreshInstruments, refreshMarket] = useUnit([
    $instruments,
    $instrumentsLoaded,
    $watchlist,
    instrumentsRefreshed,
    marketRefreshed,
  ]);
  useEffect(() => {
    refreshInstruments();
    refreshMarket();
  }, [refreshInstruments, refreshMarket]);
  const market = new Map((watchlist ?? []).map((w) => [w.instrument.id, w]));

  const columns: Column<Instrument>[] = [
    {
      id: 'symbol',
      header: t('watchlist.symbol'),
      cell: (i) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{i.display_symbol}</span>
          {!i.active && <Pill>{t('watchlist.inactive')}</Pill>}
        </span>
      ),
    },
    {
      id: 'isin',
      header: t('watchlist.isin'),
      cell: (i) => <span className="tabular text-text-2">{i.isin}</span>,
    },
    {
      id: 'name',
      header: t('watchlist.name'),
      cell: (i) => <span className="block max-w-64 truncate">{i.name}</span>,
    },
    {
      id: 'signal',
      header: t('watchlist.signal'),
      cell: (i) => <span className="text-text-2">{i.signal_symbol}</span>,
    },
    {
      id: 'last',
      header: t('watchlist.last'),
      numeric: true,
      cell: (i) => {
        const quote = market.get(i.id)?.quote;
        return quote ? formatMoney(quote.price, locale, { places: placesOf(i.tick_size) }) : '—';
      },
    },
    {
      id: 'day',
      header: t('watchlist.day'),
      numeric: true,
      cell: (i) => {
        const change = market.get(i.id)?.quote?.day_change_pct ?? null;
        return change === null ? (
          '—'
        ) : (
          <PlValue value={toNumber(change)}>
            {formatPercent(change, locale, { signed: true })}
          </PlValue>
        );
      },
    },
    {
      id: 'drawdown',
      header: t('watchlist.drawdown'),
      numeric: true,
      cell: (i) => {
        const regime = market.get(i.id)?.regime;
        return regime ? formatRatio(regime.drawdown, locale) : '—';
      },
    },
    {
      id: 'trend',
      header: t('watchlist.trend'),
      cell: (i) => {
        const regime = market.get(i.id)?.regime;
        return regime ? <TrendPill regime={regime} /> : '—';
      },
    },
    {
      id: 'volatility',
      header: t('watchlist.volatility'),
      cell: (i) => {
        const regime = market.get(i.id)?.regime;
        return regime ? <VolatilityPill regime={regime} /> : '—';
      },
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      cell: (i) => <RowMenu instrument={i} />,
    },
  ];

  return (
    <AppShell page="watchlist" crumbs={[t('nav.watchlist')]}>
      <div className="mx-auto w-full max-w-6xl space-y-4 px-6 py-6 max-sm:px-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold">{t('nav.watchlist')}</h1>
          <Button
            leadingIcon={<Plus />}
            onClick={() => {
              addInstrumentOpened();
            }}
          >
            {t('watchlist.add')}
          </Button>
        </div>
        {!loaded ? (
          <div className="flex justify-center py-16">
            <Spinner label={t('common.loading')} />
          </div>
        ) : instruments.length === 0 ? (
          <EmptyState
            className="py-16"
            icon={<Eye />}
            title={t('watchlist.emptyTitle')}
            body={t('watchlist.emptyBody')}
            action={
              <Button
                variant="primary"
                leadingIcon={<Plus />}
                onClick={() => {
                  addInstrumentOpened();
                }}
              >
                {t('watchlist.add')}
              </Button>
            }
          />
        ) : (
          <section className="overflow-x-auto rounded-lg border border-border-subtle bg-bg-1 px-1 py-1">
            <DataTable
              caption={t('nav.watchlist')}
              columns={columns}
              rows={instruments}
              rowKey={(i) => i.id}
            />
          </section>
        )}
        <p className="text-xs text-text-3">{t('watchlist.regimeNote')}</p>
      </div>
    </AppShell>
  );
}

function RowMenu({ instrument }: { instrument: Instrument }) {
  const { t } = useTranslation();
  const [update, remove, toast] = useUnit([updateInstrumentFx, deleteInstrumentFx, toastShown]);
  return (
    <span className="flex justify-end">
      <Menu>
        <MenuTrigger asChild>
          <IconButton
            size="sm"
            label={t('watchlist.actionsFor', { symbol: instrument.display_symbol })}
          >
            <MoreHorizontal />
          </IconButton>
        </MenuTrigger>
        <MenuContent>
          {instrument.active && (
            <MenuItem
              icon={<Plus />}
              onSelect={() => {
                openPositionRequested({ instrumentId: instrument.id });
              }}
            >
              {t('positions.new')}
            </MenuItem>
          )}
          <MenuItem
            icon={<Power />}
            onSelect={() => {
              void update({ id: instrument.id, patch: { active: !instrument.active } }).catch(
                () => undefined,
              );
            }}
          >
            {instrument.active ? t('watchlist.deactivate') : t('watchlist.activate')}
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            danger
            icon={<Trash2 />}
            onSelect={() => {
              confirmRequested({
                title: t('watchlist.removeTitle', { symbol: instrument.display_symbol }),
                body: t('watchlist.removeBody'),
                confirmLabel: t('watchlist.remove'),
                danger: true,
                onConfirm: () =>
                  remove(instrument.id).catch((error: unknown) => {
                    toast({
                      message: error instanceof Error ? error.message : String(error),
                      tone: 'error',
                    });
                    throw error;
                  }),
              });
            }}
          >
            {t('watchlist.remove')}
          </MenuItem>
        </MenuContent>
      </Menu>
    </span>
  );
}
